import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { CalendarRange, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Calendar } from '@/components/atoms/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { cn } from '@/lib/utils';

interface EntityListDateFilterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  range: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  onClear: () => void;
}

export function EntityListDateFilter({
  open,
  onOpenChange,
  range,
  onSelect,
  onClear,
}: EntityListDateFilterProps) {
  const hasDateFilter = !!(range?.from || range?.to);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn('h-8 w-8 shrink-0', hasDateFilter && 'border-primary text-primary')}
        >
          <CalendarRange size={13} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <Calendar
          mode="range"
          selected={range}
          onSelect={onSelect}
          numberOfMonths={1}
          initialFocus
        />
        {hasDateFilter && (
          <div className="border-t px-3 py-2 flex justify-end">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClear}>
              <X size={11} className="mr-1" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function EntityListActiveDatePill({
  range,
  onClear,
}: {
  range: DateRange | undefined;
  onClear: () => void;
}) {
  const hasDateFilter = !!(range?.from || range?.to);
  if (!hasDateFilter) return null;

  return (
    <div className="px-3 py-1.5 border-b flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">
        {range?.from && range?.to
          ? `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`
          : range?.from
            ? `From ${format(range.from, 'MMM d, yyyy')}`
            : `Until ${format(range!.to!, 'MMM d, yyyy')}`}
      </span>
      <button onClick={onClear} className="ml-auto text-muted-foreground hover:text-foreground">
        <X size={11} />
      </button>
    </div>
  );
}
